/**
 * The full preview: the card large and clean - no numbers, outlines or tints -
 * step by step in the order the person receiving it opens it, then the sheet
 * as it will be printed. Keyboard: arrows to step, Escape to close.
 *
 * 2026-10-17, as the owner asked: Next is the obvious thing to press - a big button,
 * and big arrows either side of the card - and the way back is a plain "Back to
 * editing" button rather than a small ×.
 */
import { useEffect, useState } from 'react';
import type { PaperStyle, TemplateDocument, ZoneSymbol, ZoneText } from '@foldmycard/scene';
import { JourneyView, SheetFace, type InviteCode, type ShownImage } from './card-view.tsx';
import { t } from './strings.ts';

interface Props {
  template: TemplateDocument;
  pictures: Map<number, ShownImage>;
  notes: Map<number, ZoneText>;
  /** The symbols in symbol frames (symbol.tsx). */
  symbols?: Map<number, ZoneSymbol>;
  paper?: PaperStyle;
  mono?: boolean;
  plain?: Set<number>;
  /** An invitation's RSVP code (invite.tsx). */
  invite?: InviteCode;
  onClose: () => void;
  startAt?: string;
}

const Arrow = ({ d }: { d: string }) => <svg className="btn-icon" viewBox="0 0 24 24" aria-hidden="true"><path d={d} /></svg>;

export function PreviewDialog({ template, pictures, notes, symbols, paper, mono, plain, invite, onClose, startAt }: Props) {
  const steps = [...template.views.map((v) => v.id), 'sheet'];
  const [i, setI] = useState(Math.max(0, steps.indexOf(startAt || steps[0])));
  const step = steps[i];
  const view = template.views.find((v) => v.id === step);
  const card = { template, pictures, notes, symbols, paper, mono, plain, invite, selected: null, mode: 'clean' as const };
  const back = () => setI((x) => Math.max(0, x - 1));
  const next = () => setI((x) => Math.min(steps.length - 1, x + 1));
  const last = i === steps.length - 1;

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setI((x) => Math.min(steps.length - 1, x + 1));
      if (e.key === 'ArrowLeft') setI((x) => Math.max(0, x - 1));
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [onClose, steps.length]);

  return (
    <div className="preview" role="dialog" aria-modal="true" aria-label={t('preview')}>
      <header>
        <span className="preview-step">{i + 1}<span className="muted">/{steps.length}</span></span>
        <div className="preview-title">
          <strong>{view ? view.title : t('viewSheet')}</strong>
          <span className="muted small">{view ? view.note : t('sheetNote')}</span>
        </div>
        <button className="preview-close" onClick={onClose}>
          <Arrow d="M6 6l12 12M18 6L6 18" />{t('backToEditing')}
        </button>
      </header>
      <div className="preview-stage">
        <button className="preview-side left" onClick={back} disabled={i === 0} aria-label={t('previous')} title={t('previous')}>
          <Arrow d="M15 5l-7 7 7 7" />
        </button>
        <div className="preview-card">
          {view ? <JourneyView {...card} view={view} /> : (
            <div className="sheet-pair">
              {([1, 2] as const).map((f) => (
                <div key={f}><h3 className="small">{t('sheetFace', { n: f })}</h3><SheetFace {...card} face={f} /></div>
              ))}
            </div>
          )}
        </div>
        <button className="preview-side right" onClick={next} disabled={last} aria-label={t('next')} title={t('next')}>
          <Arrow d="M9 5l7 7-7 7" />
        </button>
      </div>
      <footer>
        <button onClick={back} disabled={i === 0}><Arrow d="M15 5l-7 7 7 7" />{t('previous')}</button>
        <span className="dots">
          {steps.map((s, k) => (
            <button key={s} className={k === i ? 'on' : ''} aria-label={`${k + 1}`} aria-current={k === i} onClick={() => setI(k)} />
          ))}
        </span>
        {last
          ? <button className="primary preview-next" onClick={onClose}>{t('backToEditing')}</button>
          : <button className="primary preview-next" onClick={next}>{t('next')}<Arrow d="M9 5l7 7-7 7" /></button>}
      </footer>
    </div>
  );
}
